import React, { useEffect, useState } from 'react';
import {
  FaIdCard, FaUserCheck, FaTimesCircle, FaDownload, FaPrint,
  FaSearch, FaCalendar, FaClock, FaEye, FaUser, FaMapMarkerAlt,
  FaEnvelope, FaPhone, FaTint, FaBuilding, FaGlobe, FaLink,
  FaCheckCircle, FaTimes, FaSpinner, FaArrowLeft, FaArrowRight,
  FaFilter, FaSync, FaEdit, FaTrash, FaExclamationTriangle
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const IDCardsManagement = () => {
  const [idCards, setIdCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Modal state for rejection (must be at top level)
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectCardInfo, setRejectCardInfo] = useState(null);
  // Search and filter states
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Spinner state for approval
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);

  // Modal state for view details popup
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  // Filter and pagination logic
  const getFilteredCards = () => {
    let filtered = idCards.filter(card => {
      const matchesSearch = !search ||
        (card.name && card.name.toLowerCase().includes(search.toLowerCase())) ||
        (card.email && card.email.toLowerCase().includes(search.toLowerCase())) ||
        (card.mobileNo && card.mobileNo.toString().includes(search)) ||
        (card.reporter?.iinsafId && card.reporter.iinsafId.toLowerCase().includes(search.toLowerCase()));

      return matchesSearch;
    });

    // Sort by latest first (most recent updatedAt/createdAt)
    filtered.sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB - dateA; // Descending order (latest first)
    });

    return filtered;
  };

  const filteredCards = getFilteredCards();
  const totalPages = Math.ceil(filteredCards.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCards = filteredCards.slice(startIndex, endIndex);

  // Get the id cards from the backend using api
  useEffect(() => {
    const fetchIdCards = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/icards`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched ID Cards Data:", res);



        // Update this part to handle the nested structure
        const cardsData = res.data.reportersIdCard || res.data.data || res.data || [];


        if (Array.isArray(cardsData)) {
          setIdCards(cardsData);

        } else {
          console.warn("API response is not an array:", cardsData);
          setIdCards([]);
        }
      } catch (err) {
        console.error("Error fetching ID cards:", err);
        const errorMessage = err.response?.data?.message || err.message || "Failed to fetch ID cards";
        setError(errorMessage);
        toast.error(errorMessage);
        setIdCards([]); // Ensure idCards is always an array even on error
      } finally {
        setLoading(false);
      }
    };

    fetchIdCards();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6">
        <div className="w-full px-6">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="relative mb-6 w-20 h-20 grid place-items-center mx-auto">
                <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-600 animate-spin" style={{ borderWidth: "4px" }} />
                <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-blue-500 animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
                <span className="absolute inset-0 m-4 rounded-full border-4 border-transparent border-r-blue-400 animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Loading ID Card Requests</h3>
              <p className="text-gray-500">Please wait while we fetch the data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 py-6">
        <div className="w-full px-6">
          <div className="flex justify-center items-center h-96">
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-red-200">
              <FaExclamationTriangle className="text-6xl text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-700 mb-2">Error Loading Data</h3>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Function to handle ID card verification
  const handleVerifyIdCard = async (id) => {
    console.log("Verifying ID card with ID:", id);
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/idcards/approve/${id}`, {}, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      console.log("ID card verification response:", response.data);
      if (response.status === 200) {
        toast.success('ID card verified successfully!');
        setIdCards(prevCards =>
          prevCards.map(card =>
            card._id === id ? { ...card, status: 'approved' } : card
          )
        );
        setTimeout(() => {
          setShowApproveSpinner(false);
          window.location.reload();
        }, 1200);
      } else {
        setShowApproveSpinner(false);
      }
    } catch (error) {
      setShowApproveSpinner(false);
      console.error("Error verifying ID card:", error);
      toast.error('Error verifying ID card. Please try again.');
    }
  }

  // ...existing code...

  // Open modal for rejection
  const openRejectModal = (card) => {
    setRejectCardInfo(card);
    setRejectReason('');
    setShowRejectModal(true);
  };

  // Open modal for view details
  const openDetailsModal = (card) => {
    setSelectedCard(card);
    setShowDetailsModal(true);
  };

  // Handle actual rejection with reason
  const handleReject = async (id, reason) => {
    console.log("Rejecting ID card with ID:", id, "Reason:", reason);
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/idcards/reject/${id}`,
        { rejectNote: reason }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response.status === 200) {
        toast.success('ID card rejected successfully!');
        setIdCards(prevCards =>
          prevCards.map(card =>
            card._id === id ? { ...card, status: 'rejected', rejectReason: reason } : card
          )
        );
        setTimeout(() => {
          setShowApproveSpinner(false);
          window.location.reload();
        }, 1200);
      } else {
        setShowApproveSpinner(false);
      }
    } catch (error) {
      setShowApproveSpinner(false);
      console.error("Error rejecting ID card:", error);
      toast.error('Error rejecting ID card. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6">
      <div className="w-full px-6">
        {showApproveSpinner && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
              <div className="relative mb-4 w-16 h-16 grid place-items-center">
                <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin" style={{ borderWidth: "4px" }} />
                <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
                <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-[#25b09b] animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
              </div>
              <span className="text-lg font-semibold text-blue-700 tracking-wide">Processing Approval...</span>
              <span className="text-sm text-gray-500 mt-2">Please wait while we update the status.</span>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-6 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <FaIdCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">ID Card Requests</h1>
                  <p className="text-blue-100 mt-1">Manage and review ID card applications</p>
                </div>
              </div>
              <div className="text-right text-white">
                <div className="text-2xl font-bold">{filteredCards.length}</div>
                <div className="text-blue-100 text-sm">Total Requests</div>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    placeholder="Search by name, email, mobile, or IINSAF ID..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FaSync className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {paginatedCards.length > 0 ? (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaUser className="w-4 h-4" />
                          User
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaIdCard className="w-4 h-4" />
                          IINSAF ID
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="w-4 h-4" />
                          Contact
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaMapMarkerAlt className="w-4 h-4" />
                          Location
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaClock className="w-4 h-4" />
                          Updated At
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaCalendar className="w-4 h-4" />
                          Date
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaCheckCircle className="w-4 h-4" />
                          Status
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <div className="flex items-center gap-2">
                          <FaGlobe className="w-4 h-4" />
                          Channels
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCards.map((card, index) => (
                      <tr key={card._id || card.id || index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              {card.image ? (
                                <img
                                  src={card.image}
                                  alt={card.name || 'User'}
                                  className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                                  <FaUser className="w-6 h-6 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{card.name || 'Unknown'}</div>
                              <div className="text-sm text-gray-500">{card.designation || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900 bg-gray-100 px-3 py-1 rounded-lg inline-block">
                            {card.reporter?.iinsafId || 'Not Assigned'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{card.email || 'N/A'}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-1">
                            <FaPhone className="w-3 h-3" />
                            {card.mobileNo || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{card.city || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{card.state || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {card.updatedAt ? new Date(card.updatedAt).toLocaleDateString('en-GB') : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {card.updatedAt ? new Date(card.updatedAt).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            }) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {card.createdAt ? new Date(card.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${card.status === 'Active' || card.status === 'approved' ? 'bg-green-100 text-green-800' :
                            card.status === 'Pending' || card.status === 'pending' || card.status === 'Under Review' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                            {card.status || 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {card.channelLinks && card.channelLinks.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {card.channelLinks.slice(0, 2).map((link, linkIndex) => (
                                <a
                                  key={linkIndex}
                                  href={link.link || link.url || link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                                >
                                  <FaLink className="w-3 h-3 mr-1" />
                                  {link.platform || `Link ${linkIndex + 1}`}
                                </a>
                              ))}
                              {card.channelLinks.length > 2 && (
                                <span className="text-xs text-gray-500">+{card.channelLinks.length - 2} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">No channels</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => openDetailsModal(card)}
                              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <FaEye className="w-3 h-3 mr-1" />
                              View
                            </button>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleVerifyIdCard(card._id)}
                                className="inline-flex items-center px-2 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <FaCheckCircle className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => openRejectModal(card)}
                                className="inline-flex items-center px-2 py-1 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
                              >
                                <FaTimes className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {startIndex + 1} to {Math.min(endIndex, filteredCards.length)} of {filteredCards.length} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <FaArrowLeft className="w-3 h-3" />
                        Previous
                      </button>
                      <div className="flex space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg ${currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <FaArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaIdCard className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No ID Card Requests Found</h3>
              <p className="text-gray-500 mb-4">
                {search
                  ? "No requests match your current search."
                  : "No ID card applications have been submitted yet."
                }
              </p>
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <FaTimes className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Reject ID Card Request</h3>
                </div>
                <button
                  className="text-white hover:text-red-200 transition-colors"
                  onClick={() => setShowRejectModal(false)}
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-3 flex items-center gap-2">
                  <FaExclamationTriangle className="w-4 h-4 text-red-500" />
                  Reason for rejection:
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-xl p-4 resize-none focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={4}
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this ID card request..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  className="px-6 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors font-medium"
                  onClick={() => setShowRejectModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={`px-6 py-3 rounded-xl font-medium transition-colors ${rejectReason.trim()
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-400 cursor-not-allowed text-white'
                    }`}
                  disabled={!rejectCardInfo || !rejectReason.trim()}
                  onClick={() => {
                    if (!rejectReason.trim()) {
                      toast.error('Please enter the reason for rejection');
                      return;
                    }
                    if (rejectCardInfo && rejectReason.trim()) {
                      handleReject(rejectCardInfo._id, rejectReason.trim());
                      setShowRejectModal(false);
                    }
                  }}
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ID Card Details Modal */}
      {showDetailsModal && selectedCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-6 py-6 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <FaIdCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">ID Card Details</h2>
                  <p className="text-blue-100">Complete information about the ID card request</p>
                </div>
              </div>
              <button
                className="text-white hover:text-blue-200 transition-colors p-2 rounded-lg hover:bg-white hover:bg-opacity-10"
                onClick={() => setShowDetailsModal(false)}
                title="Close"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-8">
              {/* Personal Information Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                    <FaUser className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Personal Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="flex items-center space-x-4 col-span-1 md:col-span-2 lg:col-span-3">
                    {selectedCard.image ? (
                      <img
                        src={selectedCard.image}
                        alt={selectedCard.name}
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center border-4 border-white shadow-lg">
                        <FaUser className="w-10 h-10 text-white" />
                      </div>
                    )}
                    <div>
                      <h4 className="text-2xl font-bold text-gray-800">{selectedCard.name || 'N/A'}</h4>
                      <p className="text-lg text-gray-600">{selectedCard.designation || 'N/A'}</p>
                      <div className="mt-2">
                        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${selectedCard.status === 'Active' || selectedCard.status === 'approved' ? 'bg-green-100 text-green-800' :
                          selectedCard.status === 'Pending' || selectedCard.status === 'pending' || selectedCard.status === 'Under Review' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                          {selectedCard.status || 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaEnvelope className="w-4 h-4" />
                      Email
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.email || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaPhone className="w-4 h-4" />
                      Mobile Number
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.mobileNo || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaCalendar className="w-4 h-4" />
                      Date of Birth
                    </label>
                    <p className="text-gray-800 font-medium">
                      {selectedCard.dateOfBirth ? new Date(selectedCard.dateOfBirth).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaTint className="w-4 h-4" />
                      Blood Group
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.bloodGroup || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaIdCard className="w-4 h-4" />
                      Aadhar Number
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.aadharNo || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Address Information Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                    <FaMapMarkerAlt className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Address Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaBuilding className="w-4 h-4" />
                      Residential Address
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.ResidentialAddress || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaMapMarkerAlt className="w-4 h-4" />
                      City
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.city || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaGlobe className="w-4 h-4" />
                      State
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.state || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaIdCard className="w-4 h-4" />
                      Pincode
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.pincode || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Channel Information Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                    <FaGlobe className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Channel Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaGlobe className="w-4 h-4" />
                      Channel Name
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.channelName || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaIdCard className="w-4 h-4" />
                      Channel Type
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.channelType || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaLink className="w-4 h-4" />
                      Platform
                    </label>
                    <p className="text-gray-800 font-medium">{selectedCard.plateform || selectedCard.platform || 'N/A'}</p>
                  </div>
                  {selectedCard.channelLogo && (
                    <div className="md:col-span-2 lg:col-span-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                      <label className="block text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                        <FaIdCard className="w-4 h-4" />
                        Channel Logo
                      </label>
                      <img
                        src={selectedCard.channelLogo}
                        alt="Channel Logo"
                        className="h-24 w-24 rounded-xl object-cover border-2 border-gray-200 shadow-sm"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Channel Links Section */}
              {selectedCard.channelLinks && selectedCard.channelLinks.length > 0 && (
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center">
                      <FaLink className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Channel Links</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedCard.channelLinks.map((link, index) => (
                      <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                        <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                          <FaLink className="w-4 h-4" />
                          {link.platform || `Link ${index + 1}`}
                        </label>
                        <a
                          href={link.link || link.url || link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 break-all font-medium"
                        >
                          {link.link || link.url || link}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status Information Section */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-600 rounded-xl flex items-center justify-center">
                    <FaCheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Status Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaCheckCircle className="w-4 h-4" />
                      Current Status
                    </label>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${selectedCard.status === 'Active' || selectedCard.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedCard.status === 'Pending' || selectedCard.status === 'pending' || selectedCard.status === 'Under Review' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                      {selectedCard.status || 'Pending'}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaIdCard className="w-4 h-4" />
                      IINSAF ID
                    </label>
                    <p className="text-gray-800 font-medium font-mono">{selectedCard.reporter?.iinsafId || 'Not Assigned'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaCalendar className="w-4 h-4" />
                      Created Date
                    </label>
                    <p className="text-gray-800 font-medium">
                      {selectedCard.createdAt ? new Date(selectedCard.createdAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <label className="block text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <FaClock className="w-4 h-4" />
                      Updated Date
                    </label>
                    <p className="text-gray-800 font-medium">
                      {selectedCard.updatedAt ? new Date(selectedCard.updatedAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                {selectedCard.rejectNote && (
                  <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
                    <label className="block text-sm font-semibold text-red-600 mb-2 flex items-center gap-2">
                      <FaExclamationTriangle className="w-4 h-4" />
                      Rejection Note
                    </label>
                    <p className="text-red-700 font-medium">{selectedCard.rejectNote}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IDCardsManagement;